import React, { useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllTenantUserAction, updateTenantAction, clearUpdateTenantAction } from '../../store/actions';
import toast from 'react-hot-toast';
import * as yup from 'yup';

function UserDetailsView() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = location?.state?.userId;
  const { successData, loading, error, errorInfo } = useSelector((state) => state.getAllTenantUserReducer || {});
  const { successData: updateSuccessData, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateTenantReducer || {});
  const [userData, setUserData] = useState({ tenant: {}, adminUser: {} });
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    companyName: '',
    companyPhone: '',
    companyAddress: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    notes: '',
  });

  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [errors, setErrors] = useState({});

  const validationSchema = yup.object({
    companyName: yup.string().trim().required('Company Name is required'),
    companyPhone: yup.string().trim().required('Company Phone is required'),
    companyAddress: yup.string().trim().required('Company Address is required'),
    city: yup.string().trim().required('City is required'),
    state: yup.string().trim().required('State is required'),
    country: yup.string().trim().required('Country is required'),
    zipCode: yup.string().trim().required('Zip Code is required'),
    notes: yup.string().nullable(),
  });

  useEffect(() => {
    if (userId) {
      dispatch(getAllTenantUserAction({ endPoint: `tenant/${userId}` }));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    const Success = successData?.data;
    if (Success && typeof Success === 'object') {
      const mapped = {
        tenant: {
          name: Success.companyName || '',
          company_name: Success.companyName || '',
          company_address: Success.companyAddress || '',
          financial_year: [],
          subscription_status: (Success.status || '').toString(),
          subscription_end_date: Success.subscription_end_date || '',
          is_gt: false,
          logo: Success.logoUrl || '',
          tenantId: Success.id || '',
          city: Success.city || '',
          state: Success.state || '',
          country: Success.country || '',
          zipCode: Success.zipCode || '',
          companyEmail: Success.companyEmail || '',
          companyPhone: Success.companyPhone || '',
          dbName: Success.dbName || '',
          createdAt: Success.createdAt || '',
        },
        adminUser: {
          email: Success.adminEmail || '',
          phone: Success.adminPhoneNumber || '',
          status: (Success.status || '').toString().toLowerCase() === 'active',
          firstName: Success.adminFirstName || '',
          lastName: Success.adminLastName || '',
        },
      };
      setUserData(mapped);
      setForm((prev) => ({
        ...prev,
        companyName: mapped.tenant.company_name,
        companyPhone: mapped.tenant.companyPhone || '',
        companyAddress: mapped.tenant.company_address,
        city: mapped.tenant.city || '',
        state: mapped.tenant.state || '',
        country: mapped.tenant.country || '',
        zipCode: mapped.tenant.zipCode || '',
        notes: mapped.tenant.notes || '',
      }));
      setImagePreview(mapped.tenant.logo || '');
    }
  }, [successData]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleEdit = () => {
    setEditMode(true);
    setForm((prev) => ({ ...prev }));
  };

  const handleCancel = () => {
    setEditMode(false);
    if (userData) {
      setForm({
        companyName: userData.tenant?.company_name || '',
        companyPhone: userData.tenant?.companyPhone || '',
        companyAddress: userData.tenant?.company_address || '',
        city: userData.tenant?.city || '',
        state: userData.tenant?.state || '',
        country: userData.tenant?.country || '',
        zipCode: userData.tenant?.zipCode || '',
        notes: userData.tenant?.notes || '',
      });
      setImagePreview(userData.tenant?.logo || '');
      setImageError('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1000000) {
      setImageError('File too large. Maximum size is 1MB.');
      return;
    }
    if (!['image/jpg', 'image/jpeg', 'image/png'].includes(file.type)) {
      setImageError('Unsupported file format. Please upload JPG, JPEG, or PNG images.');
      return;
    }
    setImageError('');
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      await validationSchema.validate(form, { abortEarly: false });
      setErrors({});

      const payload = {
        endPoint: `tenant/${userData?.tenant?.tenantId || userId}`,
        companyName: form.companyName,
        companyPhone: form.companyPhone,
        companyAddress: form.companyAddress,
        city: form.city,
        state: form.state,
        country: form.country,
        zipCode: form.zipCode,
        notes: form.notes,
        // companyLogo: imagePreview || userData?.tenant?.logo || '',
      };
      dispatch(updateTenantAction(payload));

      setUserData((prev) => ({
        ...prev,
        adminUser: {
          ...prev.adminUser,
        },
        tenant: {
          ...prev.tenant,
          name: form.companyName,
          company_name: form.companyName,
          company_address: form.companyAddress,
          companyPhone: form.companyPhone,
          city: form.city,
          state: form.state,
          country: form.country,
          zipCode: form.zipCode,
          notes: form.notes,
          logo: imagePreview || prev.tenant.logo,
        },
      }));
      setEditMode(false);
      setForm((prev) => ({ ...prev }));
    } catch (err) {
      if (err && err.inner) {
        const fieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path) fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
      }
      toast.error('Please fill all required fields');
    }
  };
  const { tenant, adminUser } = userData;

  useEffect(() => {
    if (updateSuccessData) {
      toast.success('Tenant updated successfully');
      dispatch(clearUpdateTenantAction());
      navigate(-1);
    }
    if (updateError) {
      toast.error(updateErrorInfo || 'Failed to update tenant');
    }
  }, [updateSuccessData, updateError, updateErrorInfo]);


  return (
    <>
      {/* UI-only: no async loaders */}
      <Box maxWidth={900} mx="auto" mt={8} p={4} ml={{ xs: 0, sm: 0, md: '22.5%' }} sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" mb={4} textAlign="center" sx={{ color: '#2563EB' }}>
            User Details
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', mb: 4, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ color: '#2563EB', borderColor: '#2563EB' }}>
              Back
            </Button>
          </Box>
        </Box>
        {loading && (
          <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2">Loading tenant details...</Typography>
          </Paper>
        )}
        {!loading && error && (
          <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="error">{errorInfo || 'Failed to load tenant details'}</Typography>
          </Paper>
        )}
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Inline Image Upload */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed rgba(0,0,0,0.3)',
              borderRadius: '12px',
              p: 2,
              width: '300px',
              margin: '0 auto 16px auto'
            }}>
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Logo Preview"
                  style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', marginBottom: 8 }}
                />
              ) : (
                <Avatar sx={{ width: 60, height: 60, mb: 1 }} />
              )}

              <Button
                variant="contained"
                component="label"
                disabled={!editMode}
                sx={{ fontSize: '0.9rem', textTransform: 'none', backgroundColor: '#2563EB' }}
              >
                {imagePreview ? 'Change Logo' : 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/jpg,image/jpeg,image/png"
                  onChange={handleImageChange}
                />
              </Button>
              {imageError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Inline Editable Fields & Read-only Admin */}
          <Grid container spacing={2}>
            {/* Admin info (read-only) */}
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Admin First Name"
                value={adminUser?.firstName || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Admin Last Name"
                value={adminUser?.lastName || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Admin Email"
                value={adminUser?.email || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Admin Phone"
                value={adminUser?.phone || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Company Name"
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.companyName)}
                helperText={errors.companyName}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Company Phone"
                name="companyPhone"
                value={form.companyPhone}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.companyPhone)}
                helperText={errors.companyPhone}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Company Address"
                name="companyAddress"
                value={form.companyAddress}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.companyAddress)}
                helperText={errors.companyAddress}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Company Email"
                value={tenant?.companyEmail || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Company Phone"
                value={tenant?.companyPhone || ''}
                fullWidth
                margin="normal"
                disabled
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.city)}
                helperText={errors.city}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.state)}
                helperText={errors.state}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.country)}
                helperText={errors.country}

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                label="Zip Code"
                name="zipCode"
                value={form.zipCode}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 265 } }}
                error={Boolean(errors.zipCode)}
                helperText={errors.zipCode}

              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                label="Notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                minRows={1}
                disabled={!editMode}
                sx={{ width: { xs: 290, sm: 240, md: 550 } }}

              />
            </Grid>
            {/* Read-only status display */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 600 }} gutterBottom>
                Status
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: adminUser?.status ? '#2563EB' : 'error.main' }}
              >
                {adminUser?.status ? 'Active' : 'Inactive'}
              </Typography>
            </Grid>
          </Grid>

          {/* Actions */}
          <Box mt={4} display="flex" justifyContent="flex-end" flexWrap="wrap" gap={2}>
            {!editMode ? (
              <Button variant="contained" sx={{ backgroundColor: '#2563EB', minWidth: 150 }} onClick={handleEdit}>
                Edit User
              </Button>
            ) : (
              <>
                <Button onClick={handleCancel} variant="outlined" sx={{ color: '#2563EB', borderColor: '#2563EB' }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} sx={{ backgroundColor: '#2563EB' }}>
                  Save Changes
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
}

export default UserDetailsView;
